// COSTA BLANCA WORLDS — Phase 2/3 surgical seam.
//
// Phase 2 proved one reversible WORLD -> PROPERTY ROOM -> WORLD crossing.
// Phase 3 extends the same guest host with the already-proven Property Room
// Studio entry point. It does not create another editor, renderer, movement
// system or source of truth: visitor and authoring are two modes of the same
// local Property Room guest, while Kimi remains exterior authority.

const TARGET_PROPERTY_ID = 'property.sea-01';
const GUEST_BASE_URL = '/property-room-v1/';
const ROOM_READY_TIMEOUT_MS = 30000;
// The donor Phase 2.7 authoring QA intentionally allows 90s for the complete
// authoring runtime + Studio + service layer to mount under headless/WebGL CI.
const STUDIO_READY_TIMEOUT_MS = 90000;

function clone(value) {
  return value == null ? value : structuredClone(value);
}

function guestUrl({ propertyId, token, mode }) {
  const params = new URLSearchParams({
    embedded: '1',
    propertyId,
    bridgeToken: token
  });
  if (mode === 'STUDIO') params.set('authoring', '1');
  return `${GUEST_BASE_URL}?${params.toString()}`;
}

function installStyles() {
  if (document.getElementById('cbw-phase2-bridge-css')) return;
  const style = document.createElement('style');
  style.id = 'cbw-phase2-bridge-css';
  style.textContent = `
    .cbw-room-entry { width:100%; margin-top:10px; border:1px solid #18312a; border-radius:999px; padding:11px 17px; color:#fff; background:#18312a; font-weight:700; letter-spacing:.02em; }
    .cbw-room-entry[hidden] { display:none !important; }
    .cbw-room-host { position:fixed; inset:0; z-index:1000; display:none; background:#211b15; color:#f4ede1; }
    .cbw-room-host[data-active="true"] { display:block; }
    .cbw-room-frame { position:absolute; inset:0; width:100%; height:100%; border:0; background:#211b15; opacity:0; transition:opacity .28s ease; }
    .cbw-room-host[data-ready="true"] .cbw-room-frame { opacity:1; }
    .cbw-room-chrome { position:absolute; z-index:40; left:18px; right:18px; top:16px; display:flex; justify-content:space-between; align-items:center; pointer-events:none; }
    .cbw-room-context { pointer-events:none; padding:9px 12px; border:1px solid rgba(244,237,225,.18); background:rgba(31,25,20,.72); backdrop-filter:blur(12px); font:600 10px/1.35 ui-sans-serif,system-ui; letter-spacing:.12em; text-transform:uppercase; }
    .cbw-room-actions { display:flex; gap:8px; align-items:center; pointer-events:auto; }
    .cbw-room-action { border:1px solid rgba(244,237,225,.35); border-radius:999px; padding:10px 15px; color:#f4ede1; background:rgba(31,25,20,.82); backdrop-filter:blur(12px); font:700 11px/1 ui-sans-serif,system-ui; letter-spacing:.08em; text-transform:uppercase; }
    .cbw-room-studio { border-color:rgba(183,207,190,.5); background:rgba(24,49,42,.88); }
    .cbw-room-host[data-ready="false"] .cbw-room-studio { opacity:.5; pointer-events:none; }
    .cbw-room-loading { position:absolute; inset:0; z-index:30; display:grid; place-items:center; background:radial-gradient(circle at 50% 42%, #4b4032 0, #292118 45%, #1e1914 100%); transition:opacity .25s ease; }
    .cbw-room-host[data-ready="true"] .cbw-room-loading { opacity:0; pointer-events:none; }
    .cbw-room-loading-inner { text-align:center; max-width:30rem; padding:24px; }
    .cbw-room-loading-inner span { display:block; font:700 10px/1.2 ui-sans-serif,system-ui; letter-spacing:.18em; text-transform:uppercase; color:#d7c5a9; }
    .cbw-room-loading-inner strong { display:block; margin-top:12px; font:500 clamp(28px,4vw,52px)/1 Georgia,serif; }
    .cbw-room-loading-inner small { display:block; margin-top:12px; color:#c3b6a3; font:500 12px/1.5 ui-sans-serif,system-ui; }
    .cbw-room-error { color:#f0b6a8 !important; }
  `;
  document.head.appendChild(style);
}

function makeHost() {
  const host = document.createElement('section');
  host.className = 'cbw-room-host';
  host.id = 'cbw-property-room-host';
  host.dataset.active = 'false';
  host.dataset.ready = 'false';
  host.dataset.mode = 'ROOM';
  host.setAttribute('aria-hidden', 'true');
  host.innerHTML = `
    <div class="cbw-room-loading"><div class="cbw-room-loading-inner">
      <span>WORLD → PROPERTY ROOM</span>
      <strong>Crossing the threshold</strong>
      <small data-bridge-status>Preserving your exact place in Altea…</small>
    </div></div>
    <iframe class="cbw-room-frame" title="Sarah Katerina Property Room"></iframe>
    <div class="cbw-room-chrome">
      <div class="cbw-room-context">Altea · <span data-bridge-property>${TARGET_PROPERTY_ID}</span></div>
      <div class="cbw-room-actions">
        <button type="button" class="cbw-room-action cbw-room-studio" data-room-studio>Open Studio</button>
        <button type="button" class="cbw-room-action cbw-room-exit" data-room-exit>← Return to Altea</button>
      </div>
    </div>`;
  document.body.appendChild(host);
  return host;
}

function waitForQa() {
  return new Promise((resolve) => {
    const tick = () => window.__qa ? resolve(window.__qa) : requestAnimationFrame(tick);
    tick();
  });
}

class PropertyRoomBridge {
  constructor(qa) {
    this.qa = qa;
    this.host = makeHost();
    this.frame = this.host.querySelector('.cbw-room-frame');
    this.status = this.host.querySelector('[data-bridge-status]');
    this.propertyLabel = this.host.querySelector('[data-bridge-property]');
    this.studioButton = this.host.querySelector('[data-room-studio]');
    this.returnContext = null;
    this.active = false;
    this.ready = false;
    this.mode = 'ROOM';
    this.entered = 0;
    this.exited = 0;
    this.failed = 0;
    this.studioOpened = 0;
    this.studioClosed = 0;
    this.lastError = null;
    this._render = qa.renderer.render.bind(qa.renderer);
    qa.renderer.render = (...args) => {
      if (this.active) return undefined;
      return this._render(...args);
    };
    this.host.querySelector('[data-room-exit]').addEventListener('click', () => this.exit());
    this.studioButton.addEventListener('click', () => this.toggleStudio());
    this._installWalkThreshold();
    this._installDetailThreshold();
  }

  _snapshot(propertyId, source) {
    const { authority, walker, state } = this.qa;
    return {
      propertyId,
      source,
      authority: authority.owner,
      pose: clone(authority.pose),
      walker: {
        x: walker.pos.x,
        z: walker.pos.z,
        yaw: walker.yaw,
        pitch: walker.pitch,
        locked: walker.locked
      },
      mode: state.mode,
      focusedEntityId: state.focusedEntityId,
      detailOpen: document.querySelector('#detail')?.classList.contains('open') || false,
      searchOpen: document.querySelector('#search-panel')?.classList.contains('open') || false,
      capturedAt: performance.now()
    };
  }

  _installWalkThreshold() {
    const walker = this.qa.walker;
    const previous = walker.onInteract;
    walker.onInteract = (entityId) => {
      if (entityId === TARGET_PROPERTY_ID) {
        this.enter(entityId, 'WALK_INTERACT');
        return;
      }
      previous?.(entityId);
    };
  }

  _installDetailThreshold() {
    const detail = document.querySelector('#detail');
    const actions = detail?.querySelector('.detail-actions');
    if (!detail || !actions) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cbw-room-entry';
    button.textContent = 'Enter Property Room';
    button.hidden = true;
    actions.insertAdjacentElement('afterend', button);
    const sync = () => {
      const id = this.qa.state.focusedEntityId;
      button.hidden = !(detail.classList.contains('open') && id === TARGET_PROPERTY_ID);
    };
    new MutationObserver(sync).observe(detail, { attributes: true, attributeFilter: ['class'] });
    const timer = setInterval(sync, 250);
    window.addEventListener('beforeunload', () => clearInterval(timer), { once: true });
    button.addEventListener('click', () => this.enter(TARGET_PROPERTY_ID, 'DETAIL_CTA'));
    sync();
  }

  async _loadGuest(mode, { countTransition = true } = {}) {
    if (!this.active || !this.returnContext) return false;
    this.mode = mode;
    this.ready = false;
    this.host.dataset.ready = 'false';
    this.host.dataset.mode = mode;
    this.status.classList.remove('cbw-room-error');
    this.status.textContent = mode === 'STUDIO' ? 'Opening Property Room Studio…' : 'Returning to visitor view…';
    this.studioButton.textContent = mode === 'STUDIO' ? 'View Room' : 'Open Studio';

    const token = `${Date.now()}-${this.entered}-${mode}-${this.studioOpened + this.studioClosed}`;
    this.frame.dataset.loadToken = token;
    this.frame.src = guestUrl({ propertyId: this.returnContext.propertyId, token, mode });

    try {
      await this._waitForGuest(token);
      this.ready = true;
      this.host.dataset.ready = 'true';
      this.status.textContent = mode === 'STUDIO' ? 'Studio ready' : 'Property Room ready';
      if (mode === 'ROOM') this._prepareGuest();
      if (countTransition) {
        if (mode === 'STUDIO') this.studioOpened += 1;
        else this.studioClosed += 1;
      }
      return true;
    } catch (error) {
      this.failed += 1;
      this.lastError = String(error?.message || error);
      this.status.textContent = `Guest unavailable — returning safely: ${this.lastError}`;
      this.status.classList.add('cbw-room-error');
      await new Promise((resolve) => setTimeout(resolve, 650));
      this.exit({ failed: true });
      return false;
    }
  }

  async enter(propertyId, source = 'UNKNOWN') {
    if (this.active || propertyId !== TARGET_PROPERTY_ID) return false;
    this.returnContext = this._snapshot(propertyId, source);
    this.active = true;
    this.ready = false;
    this.mode = 'ROOM';
    this.entered += 1;
    this.lastError = null;
    this.host.dataset.active = 'true';
    this.host.dataset.ready = 'false';
    this.host.dataset.mode = 'ROOM';
    this.host.setAttribute('aria-hidden', 'false');
    this.propertyLabel.textContent = propertyId;
    this.status.textContent = 'Preserving your exact place in Altea…';
    this.studioButton.textContent = 'Open Studio';

    document.exitPointerLock?.();
    this.qa.walker.enabled = false;
    this.qa.walker.keys.clear();
    document.querySelector('#world')?.setAttribute('aria-hidden', 'true');

    return this._loadGuest('ROOM', { countTransition: false });
  }

  async toggleStudio() {
    if (!this.active || !this.ready) return false;
    return this._loadGuest(this.mode === 'STUDIO' ? 'ROOM' : 'STUDIO');
  }

  _waitForGuest(token) {
    const started = performance.now();
    const timeoutMs = this.mode === 'STUDIO' ? STUDIO_READY_TIMEOUT_MS : ROOM_READY_TIMEOUT_MS;
    return new Promise((resolve, reject) => {
      const poll = () => {
        if (!this.active || this.frame.dataset.loadToken !== token) return reject(new Error('crossing cancelled'));
        try {
          const doc = this.frame.contentDocument;
          const ready = doc?.documentElement?.dataset?.iwReady === 'true';
          const failed = doc?.documentElement?.dataset?.iwError;
          if (failed) return reject(new Error(failed));
          if (ready) return resolve(true);
        } catch (error) {
          return reject(error);
        }
        if (performance.now() - started > timeoutMs) return reject(new Error(`${this.mode} ready timeout after ${timeoutMs}ms`));
        setTimeout(poll, 100);
      };
      this.frame.addEventListener('load', poll, { once: true });
      setTimeout(poll, 120);
    });
  }

  _prepareGuest() {
    try {
      const doc = this.frame.contentDocument;
      // The host owns the integrated Studio switch, avoiding two competing
      // authoring affordances while keeping the donor link untouched on its own URL.
      doc?.querySelector('.pr-author-link')?.remove();
      const enterButton = [...(doc?.querySelectorAll('button') || [])].find((el) => /entrar|enter|comenzar/i.test(el.textContent || ''));
      enterButton?.click();
    } catch { /* guest remains usable even if cosmetic preparation is blocked */ }
  }

  exit({ failed = false } = {}) {
    if (!this.active) return false;
    const ctx = this.returnContext;
    this.active = false;
    this.ready = false;
    this.mode = 'ROOM';
    this.host.dataset.ready = 'false';
    this.host.dataset.active = 'false';
    this.host.dataset.mode = 'ROOM';
    this.host.setAttribute('aria-hidden', 'true');
    this.status.classList.remove('cbw-room-error');
    this.studioButton.textContent = 'Open Studio';
    this.frame.removeAttribute('src');
    document.querySelector('#world')?.removeAttribute('aria-hidden');

    if (ctx) {
      this.qa.walker.setPose(ctx.walker);
      const owner = this.qa.authority._controllers?.has(ctx.authority) ? ctx.authority : 'OVERVIEW';
      this.qa.authority.request(owner, {
        reason: failed ? 'property-room-failed-return' : 'property-room-exact-return',
        durationMs: 0,
        endPose: clone(ctx.pose)
      });
      if (owner === 'WALK') this.qa.walker.enabled = true;
    }
    this.exited += 1;
    this.returnContext = null;
    return true;
  }

  report() {
    return {
      phase: '3-property-room-studio-access',
      targetPropertyId: TARGET_PROPERTY_ID,
      active: this.active,
      ready: this.ready,
      mode: this.mode,
      entered: this.entered,
      exited: this.exited,
      studioOpened: this.studioOpened,
      studioClosed: this.studioClosed,
      failed: this.failed,
      lastError: this.lastError,
      returnContext: clone(this.returnContext)
    };
  }
}

installStyles();
waitForQa().then((qa) => {
  const bridge = new PropertyRoomBridge(qa);
  window.__CBW_SURGERY = {
    phase: 3,
    bridge,
    enter: (propertyId = TARGET_PROPERTY_ID) => bridge.enter(propertyId, 'QA_API'),
    studio: () => bridge.toggleStudio(),
    exit: () => bridge.exit(),
    report: () => bridge.report()
  };
  document.documentElement.dataset.cbwPhase2 = 'ready';
  document.documentElement.dataset.cbwPhase3 = 'ready';
});
